import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, MapPin, Check, X, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  getItemSiteAvailability,
  updateItemSiteAvailability,
} from '@/app/services/itemAvailabilityService';
import { listSites } from '@/app/services/siteService';
import { useOrganization } from '@/app/hooks/useOrganization';
import { Item } from '@/app/types/item';
import { Site } from '@/app/types/site';
import { ItemSiteAvailability, ItemSiteAvailabilityUpdate } from '@/app/types/itemAvailability';

interface SiteAvailabilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: Item;
}

export function SiteAvailabilityModal({
  open,
  onOpenChange,
  item,
}: SiteAvailabilityModalProps) {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [siteAvailability, setSiteAvailability] = useState<Map<number, boolean>>(new Map());
  const [siteStockLevels, setSiteStockLevels] = useState<Map<number, {
    min_stock_level: number | null;
    max_stock_level: number | null;
    reorder_level: number | null;
  }>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set());

  // Fetch all sites for the organization
  const { data: sitesData, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites', currentOrganizationId],
    queryFn: () => listSites({ limit: 100 }, currentOrganizationId!),
    enabled: open && !!currentOrganizationId,
  });

  // Fetch current availability for the item
  const { data: currentAvailability, isLoading: isLoadingAvailability } = useQuery({
    queryKey: ['item-site-availability', item.id],
    queryFn: () => getItemSiteAvailability(item.id),
    enabled: open,
  });

  // Initialize availability and stock level state when data is loaded
  useEffect(() => {
    if (sitesData?.items && currentAvailability) {
      const availabilityMap = new Map<number, boolean>();
      const stockLevelsMap = new Map<number, {
        min_stock_level: number | null;
        max_stock_level: number | null;
        reorder_level: number | null;
      }>();
      
      // First, set all sites as unavailable by default with null stock levels
      sitesData.items.forEach((site) => {
        availabilityMap.set(site.id, false);
        stockLevelsMap.set(site.id, {
          min_stock_level: null,
          max_stock_level: null,
          reorder_level: null,
        });
      });
      
      // Then update with actual availability and stock level data
      currentAvailability.forEach((availability) => {
        availabilityMap.set(availability.site_id, availability.is_available);
        stockLevelsMap.set(availability.site_id, {
          min_stock_level: availability.min_stock_level ?? null,
          max_stock_level: availability.max_stock_level ?? null,
          reorder_level: availability.reorder_level ?? null,
        });
      });
      
      setSiteAvailability(availabilityMap);
      setSiteStockLevels(stockLevelsMap);
      setHasChanges(false);
    }
  }, [sitesData, currentAvailability]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ItemSiteAvailabilityUpdate) => 
      updateItemSiteAvailability(item.id, data),
    onSuccess: (response) => {
      toast.success(
        `Site availability updated: ${response.created} created, ${response.updated} updated`
      );
      queryClient.invalidateQueries({ queryKey: ['item-site-availability', item.id] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update site availability');
    },
  });

  const handleSiteToggle = (siteId: number, checked: boolean) => {
    setSiteAvailability((prev) => {
      const newMap = new Map(prev);
      newMap.set(siteId, checked);
      return newMap;
    });
    setHasChanges(true);
  };

  const handleStockLevelChange = (
    siteId: number,
    field: 'min_stock_level' | 'max_stock_level' | 'reorder_level',
    value: string
  ) => {
    setSiteStockLevels((prev) => {
      const newMap = new Map(prev);
      const currentLevels = newMap.get(siteId) || {
        min_stock_level: null,
        max_stock_level: null,
        reorder_level: null,
      };
      newMap.set(siteId, {
        ...currentLevels,
        [field]: value === '' ? null : parseFloat(value),
      });
      return newMap;
    });
    setHasChanges(true);
  };

  const toggleSiteExpanded = (siteId: number) => {
    setExpandedSites((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleSave = () => {
    const sites = Array.from(siteAvailability.entries()).map(([site_id, is_available]) => {
      const stockLevels = siteStockLevels.get(site_id) || {
        min_stock_level: null,
        max_stock_level: null,
        reorder_level: null,
      };
      return {
        site_id,
        is_available,
        min_stock_level: stockLevels.min_stock_level,
        max_stock_level: stockLevels.max_stock_level,
        reorder_level: stockLevels.reorder_level,
      };
    });

    const updateData: ItemSiteAvailabilityUpdate = {
      item_id: item.id,
      sites,
    };

    updateMutation.mutate(updateData);
  };

  const handleSelectAll = () => {
    const allSelected = Array.from(siteAvailability.values()).every((v) => v);
    setSiteAvailability((prev) => {
      const newMap = new Map(prev);
      prev.forEach((_, siteId) => {
        newMap.set(siteId, !allSelected);
      });
      return newMap;
    });
    setHasChanges(true);
  };

  const sites = sitesData?.items || [];
  const filteredSites = sites.filter((site) =>
    site.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLoading = isLoadingSites || isLoadingAvailability;
  const selectedCount = Array.from(siteAvailability.values()).filter((v) => v).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Site Availability for {item.name}
          </DialogTitle>
          <DialogDescription>
            Select which sites this item should be available at. The item will only appear in transactions for selected sites.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {/* Search and Actions Bar */}
              <div className="flex items-center justify-between gap-4">
                <Input
                  placeholder="Search sites..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="max-w-sm"
                />
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {selectedCount} of {sites.length} selected
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectedCount === sites.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>

              {/* Sites List */}
              <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                {filteredSites.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'No sites found matching your search' : 'No sites available'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredSites.map((site) => {
                      const isAvailable = siteAvailability.get(site.id) || false;
                      const isExpanded = expandedSites.has(site.id);
                      const stockLevels = siteStockLevels.get(site.id) || {
                        min_stock_level: null,
                        max_stock_level: null,
                        reorder_level: null,
                      };
                      const hasCustomStockLevels = stockLevels.min_stock_level !== null || 
                                                    stockLevels.max_stock_level !== null || 
                                                    stockLevels.reorder_level !== null;
                      
                      return (
                        <div
                          key={site.id}
                          className="rounded-lg border transition-colors"
                        >
                          <div className="flex items-center space-x-3 p-3 hover:bg-accent/50">
                            <Checkbox
                              id={`site-${site.id}`}
                              checked={isAvailable}
                              onCheckedChange={(checked) =>
                                handleSiteToggle(site.id, checked as boolean)
                              }
                            />
                            <Label
                              htmlFor={`site-${site.id}`}
                              className="flex-1 cursor-pointer flex items-center justify-between"
                            >
                              <div>
                                <div className="font-medium">{site.name}</div>
                                {site.city && (
                                  <div className="text-sm text-muted-foreground">
                                    {site.city}, {site.state}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {hasCustomStockLevels && (
                                  <Badge variant="secondary" className="text-xs">
                                    Custom Stock Levels
                                  </Badge>
                                )}
                                <Badge variant={isAvailable ? 'default' : 'outline'}>
                                  {isAvailable ? (
                                    <Check className="h-3 w-3 mr-1" />
                                  ) : (
                                    <X className="h-3 w-3 mr-1" />
                                  )}
                                  {isAvailable ? 'Available' : 'Not Available'}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSiteExpanded(site.id);
                                  }}
                                  className="p-1 h-7 w-7"
                                >
                                  <Settings className="h-4 w-4" />
                                </Button>
                              </div>
                            </Label>
                          </div>
                          
                          {isExpanded && (
                            <div className="border-t px-3 pb-3 pt-2 bg-muted/30">
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label htmlFor={`min-stock-${site.id}`} className="text-xs font-medium">
                                    Min Stock Level
                                  </Label>
                                  <Input
                                    id={`min-stock-${site.id}`}
                                    type="number"
                                    step="0.001"
                                    placeholder="Item default"
                                    value={stockLevels.min_stock_level ?? ''}
                                    onChange={(e) => handleStockLevelChange(site.id, 'min_stock_level', e.target.value)}
                                    className="mt-1 h-8 text-sm"
                                    disabled={!isAvailable}
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`max-stock-${site.id}`} className="text-xs font-medium">
                                    Max Stock Level
                                  </Label>
                                  <Input
                                    id={`max-stock-${site.id}`}
                                    type="number"
                                    step="0.001"
                                    placeholder="Item default"
                                    value={stockLevels.max_stock_level ?? ''}
                                    onChange={(e) => handleStockLevelChange(site.id, 'max_stock_level', e.target.value)}
                                    className="mt-1 h-8 text-sm"
                                    disabled={!isAvailable}
                                  />
                                </div>
                                <div>
                                  <Label htmlFor={`reorder-${site.id}`} className="text-xs font-medium">
                                    Reorder Level
                                  </Label>
                                  <Input
                                    id={`reorder-${site.id}`}
                                    type="number"
                                    step="0.001"
                                    placeholder="Item default"
                                    value={stockLevels.reorder_level ?? ''}
                                    onChange={(e) => handleStockLevelChange(site.id, 'reorder_level', e.target.value)}
                                    className="mt-1 h-8 text-sm"
                                    disabled={!isAvailable}
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                Leave empty to use Item master defaults. Site-specific values override Item defaults.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              {/* Warning for no selection */}
              {selectedCount === 0 && (
                <Alert>
                  <AlertDescription>
                    Warning: If no sites are selected, this item won't be available for any transactions.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
              >
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}